import { MainContainer } from "@flow/frontend/member/components/MainContainer";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { ActionComponentProps } from "@flow/frontend/member/plugin_api";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Divider, TextField, Typography, darken } from "@mui/material";
import { parseFullName } from "parse-full-name";
import * as React from "react";
import { SocialShareActionConfig } from "../../common/action_types";
import { recordShareAction } from "../../common/routes";
import { ShareType } from "../../common/types";
import { NativeShareButton, WhatsappShareButton, FacebookShareButton, TwitterShareButton } from "./ShareButtons";
import { handleSocialShareUrl } from "./ShareComponent";

export function ShareComponentMain(props: ActionComponentProps<SocialShareActionConfig>) {
  const [didShare, setDidShare] = React.useState(false);

  React.useEffect(() => props.dispatch.setBackground(props.custom.imageUrl), [props.custom.imageUrl]);

  const nextStep = () => {
    props.dispatch.gotoNextStep(didShare ? "completed" : "skipped", props.ctx, props.consents || []);
  };

  function handleShare(share_type: ShareType) {
    recordShareAction.call({
      context: props.ctx,
      action_guid: props.action.guid,
      share_type,
    });
    setDidShare(true);
  }
  function replaceMergeTags(text: string, name: string) {
    const memberFirstName = parseFullName(name).first || name;
    return text.replace(/{ member_name }/gm, memberFirstName);
  }

  if (props.ctx.member.name && props.custom.personalisedTitle) {
    props.custom.personalisedTitle = replaceMergeTags(props.custom.personalisedTitle, props.ctx.member.name);
  }

  return (
    <>
      <Box width="100%">
        <Box>
          <MainContainer>
            <Typography
              fontWeight="bold"
              display={{ xs: "none", md: "block" }}
              sx={{
                typography: { xs: "subtitle1", md: "h5" },
                color: memberTheme.palette.primary.main,
                marginBottom: "1rem",
              }}
            >
              {props.ctx.member.name && props.custom.personalisedTitle
                ? props.custom.personalisedTitle
                : props.custom.title}
            </Typography>
            <Typography
              fontWeight="bold"
              variant="h6"
              display={{ xs: "block", md: "none" }}
              sx={{ color: memberTheme.palette.primary.main }}
            >
              {props.ctx.member.name && props.custom.personalisedTitle
                ? props.custom.personalisedTitle
                : props.custom.title}
            </Typography>
            <Box sx={{ marginBottom: "24px" }} dangerouslySetInnerHTML={{ __html: props.custom.infoAbove }}></Box>
            <Divider variant="middle" sx={{ margin: "0px" }} />

            <Box sx={{ marginTop: "24px", marginBottom: "24px" }}>
              <Typography fontWeight="bold">
                Direct messages can be up to 4 times more likely to convince people to take part:
              </Typography>
            </Box>

            <Box sx={{ marginTop: "24px", marginBottom: "24px" }}>
              <Box>
                <Box sx={{ width: "100%", paddingTop: "15px", paddingBottom: "15px" }}>
                  <WhatsappShareButton
                    onShare={handleShare}
                    url={handleSocialShareUrl(props).whatsapp || location.href}
                    text={props.custom.shareTextWhatsAppTemplate || props.custom.shareTextTemplate}
                  />
                </Box>
              </Box>
              <Box>
                <Box sx={{ width: "100%", paddingTop: "15px", paddingBottom: "15px" }}>
                  <FacebookShareButton
                    onShare={handleShare}
                    url={handleSocialShareUrl(props).facebook || location.href}
                    text={props.custom.shareTextTemplate}
                  />
                </Box>
              </Box>
              <Box>
                <Box sx={{ width: "100%", paddingTop: "15px", paddingBottom: "15px" }}>
                  <TwitterShareButton
                    onShare={handleShare}
                    url={handleSocialShareUrl(props).twitter || location.href}
                    text={props.custom.shareTextTwitterTemplate || props.custom.shareTextTemplate}
                  />
                </Box>
              </Box>
              {
                // @ts-ignore
                navigator.share && (
                  <Box>
                    <Box sx={{ width: "100%", paddingTop: "15px", paddingBottom: "15px" }}>
                      <NativeShareButton
                        onShare={handleShare}
                        title={props.custom.title}
                        text={props.custom.emailDefaultText.trim() || props.custom.shareTextTemplate}
                        url={location.href}
                      />
                    </Box>
                  </Box>
                )
              }
              <Box justifyContent="right">
                <Box display="flex" sx={{ width: "100%", paddingTop: "15px", paddingBottom: "15px" }}>
                  <TextField
                    id="outlined-read-only-input"
                    label="Copy link"
                    defaultValue={props.custom.shortenedUrl.copy || location.href}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ width: "100%" }}
                  />
                  <Box
                    display="flex"
                    sx={{ width: "auto", justifyContent: "center", flexDirection: "column", paddingLeft: "16px" }}
                  >
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(props.custom.shortenedUrl.copy || location.href);
                      }}
                      size="medium"
                      sx={{
                        backgroundColor: memberTheme.palette.greys.grey300,
                        color: memberTheme.palette.greys.grey900,
                        ":hover": {
                          backgroundColor: darken(memberTheme.palette.greys.grey300, 0.1),
                          color: memberTheme.palette.greys.grey900,
                        },
                      }}
                    >
                      Copy
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider variant="middle" sx={{ margin: "0px" }} />

            <Box maxWidth={{ md: 540 }} sx={{ marginTop: "24px", marginBottom: "24px" }}>
              <Box sx={{ width: "100%", paddingBottom: "15px" }}>
                <Button onClick={nextStep} variant="outlined" sx={{ width: { xs: "100%", md: "auto" } }}>
                  <Typography sx={{ paddingLeft: "10px" }}>{props.custom.nextButtonText}</Typography>
                  <ChevronRightIcon />
                </Button>
              </Box>
            </Box>
          </MainContainer>
        </Box>
      </Box>
    </>
  );
}
